// src/pages/admin/TeacherReports.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import {
  collection,
  getDocs,
  query,
  where,
  orderBy
} from 'firebase/firestore';
import {
  getBrowserTimezone,
  isoToDisplayDate,
  formatISOForDisplay,
} from '../../utils/timezoneUtils';
import {
  ArrowLeft,
  Users,
  Calendar,
  Search,
  Download,
  RefreshCw,
  AlertTriangle,
  CheckSquare,
  Square,
  ChevronDown,
  ChevronUp,
  UserRound,
  BookOpen,
  Clock,
  X
} from 'lucide-react';

// ─── Date Helpers ──────────────────────────────────────────────────────────────

function toDateStr(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function formatDisplayDate(dateStr) {
  if (!dateStr) return '—';
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-CA', {
    year: 'numeric', month: 'short', day: 'numeric'
  });
}

function formatTime(t) {
  if (!t) return '';
  const [h, mi] = t.split(':');
  const hour = parseInt(h, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const display = hour % 12 === 0 ? 12 : hour % 12;
  return `${display}:${mi} ${ampm}`;
}

// ─── Date Range Presets ────────────────────────────────────────────────────────

const PRESETS = [
  {
    label: 'This Week',
    getRange: () => {
      const now = new Date();
      const start = new Date(now);
      start.setDate(now.getDate() - now.getDay());
      start.setHours(0, 0, 0, 0);
      return { start: toDateStr(start), end: toDateStr(now) };
    }
  },
  {
    label: 'Last Week',
    getRange: () => {
      const now = new Date();
      const endOfLastWeek = new Date(now);
      endOfLastWeek.setDate(now.getDate() - now.getDay() - 1);
      const startOfLastWeek = new Date(endOfLastWeek);
      startOfLastWeek.setDate(endOfLastWeek.getDate() - 6);
      return { start: toDateStr(startOfLastWeek), end: toDateStr(endOfLastWeek) };
    }
  },
  {
    label: 'This Month',
    getRange: () => {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      return { start: toDateStr(start), end: toDateStr(now) };
    }
  },
  {
    label: 'Last Month',
    getRange: () => {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end   = new Date(now.getFullYear(), now.getMonth(), 0);
      return { start: toDateStr(start), end: toDateStr(end) };
    }
  },
];

// ─── CSV Export Helpers ────────────────────────────────────────────────────────

function downloadCSV(filename, rows) {
  const csv = rows.map(r => r.map(cell => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Multi-Select Dropdown ─────────────────────────────────────────────────────

function MultiSelect({ label, items, selectedIds, onToggle, onSelectAll, onClearAll, renderLabel }) {
  const [open, setOpen] = useState(false);
  const allSelected = items.length > 0 && selectedIds.size === items.length;

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm hover:bg-gray-50 transition-colors min-w-[180px] justify-between"
      >
        <span className="text-gray-700 truncate">
          {selectedIds.size === 0
            ? `Select ${label}`
            : selectedIds.size === items.length
              ? `All ${label} (${items.length})`
              : `${selectedIds.size} ${label} selected`
          }
        </span>
        {open ? <ChevronUp size={14} className="text-gray-400 flex-shrink-0" /> : <ChevronDown size={14} className="text-gray-400 flex-shrink-0" />}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute top-full left-0 mt-1 w-72 bg-white border border-gray-200 rounded-xl shadow-xl z-20 overflow-hidden">
            {/* Select all / clear */}
            <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 bg-gray-50">
              <button
                onClick={() => { allSelected ? onClearAll() : onSelectAll(); }}
                className="text-xs text-purple-600 hover:text-purple-800 font-medium"
              >
                {allSelected ? 'Clear all' : 'Select all'}
              </button>
              <span className="text-xs text-gray-400">{selectedIds.size}/{items.length} selected</span>
            </div>
            <div className="max-h-60 overflow-y-auto">
              {items.map(item => {
                const isSelected = selectedIds.has(item.id);
                return (
                  <button
                    key={item.id}
                    onClick={() => onToggle(item.id)}
                    className={`w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left hover:bg-purple-50 transition-colors ${
                      isSelected ? 'bg-purple-50/50' : ''
                    }`}
                  >
                    {isSelected
                      ? <CheckSquare size={14} className="text-purple-600 flex-shrink-0" />
                      : <Square size={14} className="text-gray-300 flex-shrink-0" />
                    }
                    <span className="truncate text-gray-700">{renderLabel(item)}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Teacher Report Card ───────────────────────────────────────────────────────

function TeacherReportCard({ teacher, sessions, onExportCSV }) {
  const [expanded, setExpanded] = useState(true);

  // Sessions where this teacher is the actual teacher (regular or sub)
  const regularSessions = sessions.filter(s => !s.substituteTeacherId);
  const subSessions     = sessions.filter(s => !!s.substituteTeacherId);

  const totalSessions = sessions.length;
  const totalMinutes  = sessions.reduce((sum, s) => sum + (s.duration ?? 60), 0);
  const totalHours    = (totalMinutes / 60).toFixed(1);

  const browserTz = getBrowserTimezone();
  // Sort all sessions chronologically by UTC timestamp
  const sorted = [...sessions].sort((a, b) => (a.scheduledAt ?? '').localeCompare(b.scheduledAt ?? ''));

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
            style={{ backgroundColor: teacher.color ?? '#7C3AED' }}
          >
            {teacher.name?.charAt(0) ?? '?'}
          </div>
          <div>
            <p className="font-semibold text-gray-800">{teacher.name}</p>
            <p className="text-xs text-gray-500 mt-0.5">
              {totalSessions} session{totalSessions !== 1 ? 's' : ''} · {totalHours} hrs
              {regularSessions.length > 0 && (
                <span className="ml-2 text-purple-600">{regularSessions.length} regular</span>
              )}
              {subSessions.length > 0 && (
                <span className="ml-2 text-orange-600">{subSessions.length} sub</span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onExportCSV}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 text-gray-600 text-xs font-medium rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Download size={12} />
            CSV
          </button>
          <button
            onClick={() => setExpanded(e => !e)}
            className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors"
          >
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </div>
      </div>

      {/* Summary pills */}
      <div className="flex gap-3 px-5 py-3 border-b border-gray-100 flex-wrap">
        <div className="flex items-center gap-1.5 text-xs bg-purple-50 text-purple-700 px-2.5 py-1 rounded-full border border-purple-100">
          <Clock size={11} />
          {totalSessions} total sessions
        </div>
        <div className="flex items-center gap-1.5 text-xs bg-blue-50 text-blue-700 px-2.5 py-1 rounded-full border border-blue-100">
          <Clock size={11} />
          {totalHours} hrs
        </div>
        {regularSessions.length > 0 && (
          <div className="flex items-center gap-1.5 text-xs bg-gray-50 text-gray-600 px-2.5 py-1 rounded-full border border-gray-200">
            {regularSessions.length} regular
          </div>
        )}
        {subSessions.length > 0 && (
          <div className="flex items-center gap-1.5 text-xs bg-orange-50 text-orange-700 px-2.5 py-1 rounded-full border border-orange-200">
            <UserRound size={11} />
            {subSessions.length} as substitute
          </div>
        )}
      </div>

      {/* Session rows */}
      {expanded && (
        <div className="divide-y divide-gray-50">
          {/* Header row */}
          <div className="grid grid-cols-12 px-5 py-2 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wide">
            <div className="col-span-2">Date</div>
            <div className="col-span-3">Class</div>
            <div className="col-span-3">Assigned Teacher</div>
            <div className="col-span-2">Type</div>
            <div className="col-span-1 text-center">Duration</div>
            <div className="col-span-1 text-center">Makeup</div>
          </div>
          {sorted.length === 0 ? (
            <div className="px-5 py-8 text-center text-sm text-gray-400 italic">
              No completed sessions in this period.
            </div>
          ) : (
            sorted.map(s => (
              <div
                key={s.id}
                className={`grid grid-cols-12 px-5 py-3 text-sm items-center ${
                  s.substituteTeacherId ? 'bg-orange-50/30' : ''
                }`}
              >
                <div className="col-span-2 text-gray-600 text-xs">{s.scheduledAt ? isoToDisplayDate(s.scheduledAt, browserTz) : '—'}</div>
                <div className="col-span-3 text-gray-700 text-xs truncate" title={s.className ?? s.classId}>
                  {s.className ?? <span className="text-gray-400 italic">—</span>}
                </div>
                <div className="col-span-3 text-gray-600 text-xs truncate">
                  {s.substituteTeacherId
                    ? <span className="text-gray-400">{s.teacherName}</span>
                    : <span className="text-gray-700">{s.teacherName}</span>
                  }
                </div>
                <div className="col-span-2">
                  {s.substituteTeacherId ? (
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full font-medium border border-orange-200">
                      <UserRound size={10} /> Sub
                    </span>
                  ) : (
                    <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">
                      Regular
                    </span>
                  )}
                </div>
                <div className="col-span-1 text-center text-xs text-gray-500">{s.duration ?? 60}m</div>
                <div className="col-span-1 text-center">
                  {s.isMakeup && (
                    <span className="text-xs text-yellow-700">↻</span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ─── Class Report Card ─────────────────────────────────────────────────────────

function ClassReportCard({ cls, sessions, onExportCSV }) {
  const [expanded, setExpanded] = useState(true);
  const browserTz = getBrowserTimezone();

  const sorted = [...sessions].sort((a, b) => (a.scheduledAt ?? '').localeCompare(b.scheduledAt ?? ''));

  // Try to determine total sessions from enrolled students (first student's sessionsTotal)
  const enrolledStudents = cls.enrolledStudents ?? [];
  const sessionsTotal    = enrolledStudents[0]?.sessionsTotal ?? null;
  const sessionsLeft     = sessionsTotal != null ? Math.max(0, sessionsTotal - sorted.length) : null;

  const totalMinutes = sessions.reduce((sum, s) => sum + (s.duration ?? 60), 0);
  const totalHours   = (totalMinutes / 60).toFixed(1);
  const subCount     = sessions.filter(s => !!s.substituteTeacherId).length;

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
            <BookOpen size={16} className="text-purple-600" />
          </div>
          <div>
            <p className="font-semibold text-gray-800">
              {cls.teacherName}
              <span className="text-gray-400 font-normal text-sm ml-2">
                · {cls.type} · {cls.ageGroup}
              </span>
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              {cls.dayOfWeek} at {formatTime(cls.time)}
              {enrolledStudents.length > 0 && ` · ${enrolledStudents.length} student${enrolledStudents.length !== 1 ? 's' : ''}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onExportCSV}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 text-gray-600 text-xs font-medium rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Download size={12} />
            CSV
          </button>
          <button
            onClick={() => setExpanded(e => !e)}
            className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors"
          >
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </div>
      </div>

      {/* Summary pills */}
      <div className="flex gap-3 px-5 py-3 border-b border-gray-100 flex-wrap">
        <div className="flex items-center gap-1.5 text-xs bg-purple-50 text-purple-700 px-2.5 py-1 rounded-full border border-purple-100">
          <CheckSquare size={11} />
          {sorted.length}{sessionsTotal != null ? `/${sessionsTotal}` : ''} sessions completed
        </div>
        <div className="flex items-center gap-1.5 text-xs bg-blue-50 text-blue-700 px-2.5 py-1 rounded-full border border-blue-100">
          <Clock size={11} />
          {totalHours} hrs
        </div>
        {sessionsLeft != null && sessionsLeft > 0 && (
          <div className="flex items-center gap-1.5 text-xs bg-yellow-50 text-yellow-700 px-2.5 py-1 rounded-full border border-yellow-200">
            {sessionsLeft} remaining
          </div>
        )}
        {sessionsLeft === 0 && (
          <div className="flex items-center gap-1.5 text-xs bg-green-50 text-green-700 px-2.5 py-1 rounded-full border border-green-200">
            ✓ Course complete
          </div>
        )}
        {subCount > 0 && (
          <div className="flex items-center gap-1.5 text-xs bg-orange-50 text-orange-700 px-2.5 py-1 rounded-full border border-orange-200">
            <UserRound size={11} />
            {subCount} sub session{subCount !== 1 ? 's' : ''}
          </div>
        )}
      </div>

      {/* Session rows */}
      {expanded && (
        <div className="divide-y divide-gray-50">
          {/* Header row */}
          <div className="grid grid-cols-12 px-5 py-2 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wide">
            <div className="col-span-1 text-center">#</div>
            <div className="col-span-2">Date</div>
            <div className="col-span-4">Teacher</div>
            <div className="col-span-2">Type</div>
            <div className="col-span-2 text-center">Duration</div>
            <div className="col-span-1 text-center">Makeup</div>
          </div>
          {sorted.length === 0 ? (
            <div className="px-5 py-8 text-center text-sm text-gray-400 italic">
              No completed sessions from the selected start date.
            </div>
          ) : (
            sorted.map((s, idx) => (
              <div
                key={s.id}
                className={`grid grid-cols-12 px-5 py-3 text-sm items-center ${
                  s.substituteTeacherId ? 'bg-orange-50/30' : ''
                }`}
              >
                <div className="col-span-1 text-center">
                  <span className="text-xs font-semibold text-gray-400 w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center mx-auto">
                    {idx + 1}
                  </span>
                </div>
                <div className="col-span-2 text-gray-600 text-xs">{s.scheduledAt ? isoToDisplayDate(s.scheduledAt, browserTz) : '—'}</div>
                <div className="col-span-4 text-xs">
                  {s.substituteTeacherId ? (
                    <div>
                      <span className="font-medium text-gray-800">{s.substituteTeacherName}</span>
                      <span className="ml-1.5 text-orange-600 text-xs">
                        <span className="px-1.5 py-0.5 bg-orange-100 rounded font-semibold">SUB</span>
                      </span>
                      <div className="text-gray-400 text-xs mt-0.5">normally {s.teacherName}</div>
                    </div>
                  ) : (
                    <span className="text-gray-700">{s.teacherName}</span>
                  )}
                </div>
                <div className="col-span-2">
                  {s.substituteTeacherId ? (
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full font-medium border border-orange-200">
                      <UserRound size={10} /> Sub
                    </span>
                  ) : (
                    <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">
                      Regular
                    </span>
                  )}
                </div>
                <div className="col-span-2 text-center text-xs text-gray-500">{s.duration ?? 60} min</div>
                <div className="col-span-1 text-center">
                  {s.isMakeup && (
                    <span className="text-xs text-yellow-700" title="Makeup session">↻</span>
                  )}
                </div>
              </div>
            ))
          )}

          {/* Remaining sessions row */}
          {sessionsLeft != null && sessionsLeft > 0 && (
            <div className="px-5 py-3 bg-yellow-50/50 border-t border-yellow-100">
              <p className="text-xs text-yellow-700 font-medium">
                {sessionsLeft} session{sessionsLeft !== 1 ? 's' : ''} remaining in this course block
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────

function TeacherReports() {
  const navigate = useNavigate();

  // ── Base data ────────────────────────────────────────────────────────────────
  const [teachers, setTeachers] = useState([]);
  const [classes,  setClasses]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);

  // ── Mode ─────────────────────────────────────────────────────────────────────
  const [mode, setMode] = useState('teacher'); // 'teacher' | 'class'

  // ── Filters — teacher mode ────────────────────────────────────────────────────
  const [selectedTeacherIds, setSelectedTeacherIds] = useState(new Set());
  const [preset,     setPreset]     = useState('This Month');
  const [customStart, setCustomStart] = useState('');
  const [customEnd,   setCustomEnd]   = useState('');

  // ── Filters — class mode ──────────────────────────────────────────────────────
  const [selectedClassIds,  setSelectedClassIds]  = useState(new Set());
  const [classStartDate, setClassStartDate] = useState('');

  // ── Results ──────────────────────────────────────────────────────────────────
  const [results,       setResults]       = useState(null);   // null = not yet run
  const [runLoading,    setRunLoading]    = useState(false);

  // ── Load teachers + classes ──────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [tSnap, cSnap] = await Promise.all([
          getDocs(query(collection(db, 'teachers'), orderBy('name'))),
          getDocs(collection(db, 'classes')),
        ]);
        const teacherList = tSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        const classList   = cSnap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(c => c.status === 'active')
          .sort((a, b) => (a.teacherName ?? '').localeCompare(b.teacherName ?? ''));
        setTeachers(teacherList);
        setClasses(classList);
        // Default: all teachers selected
        setSelectedTeacherIds(new Set(teacherList.map(t => t.id)));
      } catch (err) {
        console.error('TeacherReports load error:', err);
        setError('Failed to load data. Please refresh.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // ── Compute active date range ─────────────────────────────────────────────────
  function getDateRange() {
    if (preset === 'Custom') {
      return { start: customStart, end: customEnd };
    }
    const p = PRESETS.find(p => p.label === preset);
    return p ? p.getRange() : { start: '', end: '' };
  }

  // ── Run report ────────────────────────────────────────────────────────────────
  const handleRun = async () => {
    setRunLoading(true);
    setError(null);
    try {
      if (mode === 'teacher') {
        await runTeacherReport();
      } else {
        await runClassReport();
      }
    } catch (err) {
      console.error('Report run error:', err);
      setError('Failed to run report. Please try again.');
    } finally {
      setRunLoading(false);
    }
  };

  const runTeacherReport = async () => {
    const { start, end } = getDateRange();
    if (!start || !end) { setError('Please select a date range.'); return; }
    if (selectedTeacherIds.size === 0) { setError('Please select at least one teacher.'); return; }

    // Convert calendar-day boundaries to UTC ISO for scheduledAt range query
    const startISO = `${start}T00:00:00.000Z`;
    const endISO   = `${end}T23:59:59.999Z`;

    const snap = await getDocs(
      query(
        collection(db, 'sessions'),
        where('status', '==', 'completed'),
        where('scheduledAt', '>=', startISO),
        where('scheduledAt', '<=', endISO),
        orderBy('scheduledAt')
      )
    );
    const allSessions = snap.docs.map(d => ({ id: d.id, ...d.data() }));

    // For each selected teacher, find sessions where they were the ACTUAL teacher
    const byTeacher = {};
    selectedTeacherIds.forEach(tid => {
      byTeacher[tid] = [];
    });

    allSessions.forEach(s => {
      const effectiveTeacherId = s.substituteTeacherId ?? s.teacherId;
      if (selectedTeacherIds.has(effectiveTeacherId)) {
        const cls = classes.find(c => c.id === s.classId);
        byTeacher[effectiveTeacherId].push({ ...s, className: cls?.teacherName ? `${cls.teacherName} · ${cls.dayOfWeek} ${formatTime(cls.time)}` : s.classId });
      }
    });

    setResults({ mode: 'teacher', byTeacher, dateRange: getDateRange() });
  };

  const runClassReport = async () => {
    if (selectedClassIds.size === 0) { setError('Please select at least one class.'); return; }
    if (!classStartDate) { setError('Please select a course start date.'); return; }

    const startISO = `${classStartDate}T00:00:00.000Z`;
    const endISO   = new Date().toISOString();

    // Fetch all completed sessions from the start date across selected classes
    const snap = await getDocs(
      query(
        collection(db, 'sessions'),
        where('status', '==', 'completed'),
        where('scheduledAt', '>=', startISO),
        where('scheduledAt', '<=', endISO),
        orderBy('scheduledAt')
      )
    );
    const allSessions = snap.docs.map(d => ({ id: d.id, ...d.data() }));

    // Group by classId (filter to only selected classes)
    const byClass = {};
    selectedClassIds.forEach(cid => { byClass[cid] = []; });

    allSessions.forEach(s => {
      if (selectedClassIds.has(s.classId)) {
        byClass[s.classId].push(s);
      }
    });

    setResults({ mode: 'class', byClass, startDate: classStartDate });
  };

  // ── CSV Export — teacher mode ─────────────────────────────────────────────────
  function exportTeacherCSV(teacher, sessions) {
    const { start, end } = getDateRange();
    const rows = [
      [`Teacher Report — ${teacher.name}`],
      [`Period: ${formatDisplayDate(start)} to ${formatDisplayDate(end)}`],
      [],
      ['Date (UTC)', 'Time (UTC)', 'Class', 'Assigned Teacher', 'Type', 'Duration (min)', 'Makeup'],
      ...sessions
        .sort((a, b) => (a.scheduledAt ?? '').localeCompare(b.scheduledAt ?? ''))
        .map(s => [
          s.scheduledAt ? isoToDisplayDate(s.scheduledAt, 'UTC') : '',
          s.scheduledAt ? formatISOForDisplay(s.scheduledAt, 'UTC') : '',
          s.className ?? s.classId,
          s.teacherName,
          s.substituteTeacherId ? 'Substitute' : 'Regular',
          s.duration ?? 60,
          s.isMakeup ? 'Yes' : 'No',
        ]),
      [],
      ['Total Sessions', sessions.length],
      ['Total Hours', (sessions.reduce((sum, s) => sum + (s.duration ?? 60), 0) / 60).toFixed(1)],
      ['Regular Sessions', sessions.filter(s => !s.substituteTeacherId).length],
      ['Substitute Sessions', sessions.filter(s => !!s.substituteTeacherId).length],
    ];
    downloadCSV(`teacher-report-${teacher.name.replace(/\s+/g, '-')}-${start}-to-${end}.csv`, rows);
  }

  // ── CSV Export — class mode ───────────────────────────────────────────────────
  function exportClassCSV(cls, sessions) {
    const sorted = [...sessions].sort((a, b) => (a.scheduledAt ?? '').localeCompare(b.scheduledAt ?? ''));
    const rows = [
      [`Class Report — ${cls.teacherName} (${cls.dayOfWeek} ${formatTime(cls.time)})`],
      [`From: ${formatDisplayDate(classStartDate)} · Type: ${cls.type} · Age: ${cls.ageGroup}`],
      [],
      ['#', 'Date (UTC)', 'Time (UTC)', 'Teacher', 'Sub Name', 'Type', 'Duration (min)', 'Makeup'],
      ...sorted.map((s, i) => [
        i + 1,
        s.scheduledAt ? isoToDisplayDate(s.scheduledAt, 'UTC') : '',
        s.scheduledAt ? formatISOForDisplay(s.scheduledAt, 'UTC') : '',
        s.teacherName,
        s.substituteTeacherName ?? '',
        s.substituteTeacherId ? 'Substitute' : 'Regular',
        s.duration ?? 60,
        s.isMakeup ? 'Yes' : 'No',
      ]),
      [],
      ['Total Sessions Completed', sorted.length],
      ['Total Hours', (sorted.reduce((sum, s) => sum + (s.duration ?? 60), 0) / 60).toFixed(1)],
    ];
    downloadCSV(`class-report-${cls.id}-from-${classStartDate}.csv`, rows);
  }

  // ── Export all teachers ───────────────────────────────────────────────────────
  function exportAllTeachersCSV() {
    if (!results || results.mode !== 'teacher') return;
    const { start, end } = getDateRange();
    const rows = [
      [`All Teachers Report — ${formatDisplayDate(start)} to ${formatDisplayDate(end)}`],
      [],
      ['Teacher', 'Date (UTC)', 'Time (UTC)', 'Class', 'Assigned Teacher', 'Type', 'Duration (min)', 'Makeup'],
    ];
    teachers
      .filter(t => selectedTeacherIds.has(t.id))
      .forEach(t => {
        const sessions = results.byTeacher[t.id] ?? [];
        sessions
          .sort((a, b) => (a.scheduledAt ?? '').localeCompare(b.scheduledAt ?? ''))
          .forEach(s => {
            rows.push([
              t.name,
              s.scheduledAt ? isoToDisplayDate(s.scheduledAt, 'UTC') : '',
              s.scheduledAt ? formatISOForDisplay(s.scheduledAt, 'UTC') : '',
              s.className ?? s.classId,
              s.teacherName,
              s.substituteTeacherId ? 'Substitute' : 'Regular',
              s.duration ?? 60,
              s.isMakeup ? 'Yes' : 'No',
            ]);
          });
        rows.push([]);
        rows.push([`${t.name} — Totals`]);
        rows.push(['Total Sessions', sessions.length]);
        rows.push(['Total Hours', (sessions.reduce((sum, s) => sum + (s.duration ?? 60), 0) / 60).toFixed(1)]);
        rows.push([]);
      });
    downloadCSV(`all-teachers-report-${start}-to-${end}.csv`, rows);
  }

  // ── Render ────────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  const { start: rangeStart, end: rangeEnd } = getDateRange();
  const teacherResults = results?.mode === 'teacher' ? results : null;
  const classResults   = results?.mode === 'class'   ? results : null;

  const selectedTeacherList = teachers.filter(t => selectedTeacherIds.has(t.id));
  const selectedClassList   = classes.filter(c => selectedClassIds.has(c.id));

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto p-6 space-y-5">

        {/* Header */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/admin')}
            className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Teacher Reports</h1>
            <p className="text-sm text-gray-500">Session history and hours by teacher or class</p>
          </div>
        </div>

        {/* Error banner */}
        {error && (
          <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
            <AlertTriangle size={16} />
            {error}
            <button onClick={() => setError(null)} className="ml-auto"><X size={14} /></button>
          </div>
        )}

        {/* Mode toggle + filters */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">

          {/* Mode tabs */}
          <div className="flex bg-gray-100 p-1 rounded-xl gap-1 w-fit">
            <button
              onClick={() => { setMode('teacher'); setResults(null); }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                mode === 'teacher' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Users size={14} />
              By Teacher
            </button>
            <button
              onClick={() => { setMode('class'); setResults(null); }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                mode === 'class' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <BookOpen size={14} />
              By Class
            </button>
          </div>

          {/* Teacher mode filters */}
          {mode === 'teacher' && (
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Teachers</label>
                <MultiSelect
                  label="Teachers"
                  items={teachers}
                  selectedIds={selectedTeacherIds}
                  onToggle={id => setSelectedTeacherIds(prev => {
                    const next = new Set(prev);
                    next.has(id) ? next.delete(id) : next.add(id);
                    return next;
                  })}
                  onSelectAll={() => setSelectedTeacherIds(new Set(teachers.map(t => t.id)))}
                  onClearAll={() => setSelectedTeacherIds(new Set())}
                  renderLabel={t => t.name}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Period</label>
                <div className="flex flex-wrap gap-1.5">
                  {PRESETS.map(p => (
                    <button
                      key={p.label}
                      onClick={() => setPreset(p.label)}
                      className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                        preset === p.label
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                  <button
                    onClick={() => setPreset('Custom')}
                    className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                      preset === 'Custom'
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    Custom
                  </button>
                </div>
              </div>
              {preset === 'Custom' && (
                <div className="flex items-center gap-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">From</label>
                    <input
                      type="date"
                      value={customStart}
                      onChange={e => setCustomStart(e.target.value)}
                      className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">To</label>
                    <input
                      type="date"
                      value={customEnd}
                      onChange={e => setCustomEnd(e.target.value)}
                      className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Class mode filters */}
          {mode === 'class' && (
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Classes</label>
                <MultiSelect
                  label="Classes"
                  items={classes}
                  selectedIds={selectedClassIds}
                  onToggle={id => setSelectedClassIds(prev => {
                    const next = new Set(prev);
                    next.has(id) ? next.delete(id) : next.add(id);
                    return next;
                  })}
                  onSelectAll={() => setSelectedClassIds(new Set(classes.map(c => c.id)))}
                  onClearAll={() => setSelectedClassIds(new Set())}
                  renderLabel={c => `${c.teacherName} · ${c.dayOfWeek} ${formatTime(c.time)}`}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Course Start Date</label>
                <input
                  type="date"
                  value={classStartDate}
                  onChange={e => setClassStartDate(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <p className="text-xs text-gray-400 self-center">Results will show all completed sessions from this date through today.</p>
            </div>
          )}

          {/* Run button */}
          <div className="flex items-center gap-3 pt-1">
            <button
              onClick={handleRun}
              disabled={runLoading}
              className="flex items-center gap-2 px-5 py-2.5 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
            >
              {runLoading
                ? <><RefreshCw size={15} className="animate-spin" /> Running...</>
                : <><Search size={15} /> Run Report</>
              }
            </button>
            {results && (
              <button
                onClick={() => setResults(null)}
                className="text-xs text-gray-400 hover:text-gray-600"
              >
                Clear results
              </button>
            )}
          </div>
        </div>

        {/* ── Teacher mode results ── */}
        {teacherResults && (
          <div className="space-y-4">
            {/* Results header */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-base font-semibold text-gray-700">
                  Results · {formatDisplayDate(teacherResults.dateRange.start)} – {formatDisplayDate(teacherResults.dateRange.end)}
                </h2>
                <p className="text-xs text-gray-400 mt-0.5">
                  {selectedTeacherList.length} teacher{selectedTeacherList.length !== 1 ? 's' : ''} ·{' '}
                  {selectedTeacherList.reduce((n, t) => n + (teacherResults.byTeacher[t.id]?.length ?? 0), 0)} total sessions
                </p>
              </div>
              {selectedTeacherList.length > 1 && (
                <button
                  onClick={exportAllTeachersCSV}
                  className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-600 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <Download size={14} />
                  Export All
                </button>
              )}
            </div>

            {/* Per-teacher cards */}
            {selectedTeacherList.map(teacher => (
              <TeacherReportCard
                key={teacher.id}
                teacher={teacher}
                sessions={teacherResults.byTeacher[teacher.id] ?? []}
                onExportCSV={() => exportTeacherCSV(teacher, teacherResults.byTeacher[teacher.id] ?? [])}
              />
            ))}
          </div>
        )}

        {/* ── Class mode results ── */}
        {classResults && (
          <div className="space-y-4">
            {/* Results header */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-base font-semibold text-gray-700">
                  Results · From {formatDisplayDate(classResults.startDate)}
                </h2>
                <p className="text-xs text-gray-400 mt-0.5">
                  {selectedClassList.length} class{selectedClassList.length !== 1 ? 'es' : ''} ·{' '}
                  {selectedClassList.reduce((n, c) => n + (classResults.byClass[c.id]?.length ?? 0), 0)} total sessions
                </p>
              </div>
            </div>

            {/* Per-class cards */}
            {selectedClassList.map(cls => (
              <ClassReportCard
                key={cls.id}
                cls={cls}
                sessions={classResults.byClass[cls.id] ?? []}
                onExportCSV={() => exportClassCSV(cls, classResults.byClass[cls.id] ?? [])}
              />
            ))}
          </div>
        )}

      </div>
    </div>
  );
}

export default TeacherReports;